import React, {useMemo, useState} from 'react'
import {generatePath, Link, Navigate, useNavigate, useParams} from "react-router-dom";
import {Badge, Input, Select} from 'antd';
import EmptyBanner from "../../components/EmptyBanner";
import LoadingBanner from "../../components/LoadingBanner";
import ErrorBanner from "../../components/ErrorBanner";

import {SimpleStatusTabs} from "../../components/nav/StatusTabs";
import Footer from "../../components/nav/Footer";

import {useTalentResourceFilters} from "../../models/AgencyOpportunity";
import Opportunity from "../../models/Opportunity";
import {useAuth} from "../../providers/AuthProvider";
import {useDebounce, useMount} from "react-use";
import {useAPI} from "../../config/API";

const {Search} = Input;
const OppStatuses = ['All', ...Opportunity.statusOptions(), 'On-Hold'];

const AgencyOpportunityList = (props) => {
    const navigate = useNavigate();
    const {status} = useParams();
    const {auth} = useAuth();
    const [filters, setFilters] = useState({matching_talent_users: [], search: ''});
    const [debouncedFilters, setDebouncedFilters] = useState({...filters});

    const talentResourceFilters = useTalentResourceFilters();

    const [isReady, cancel] = useDebounce(
        () => {
            setDebouncedFilters(filters);
            if (statusFromQuery !== 'All') {
                navigate(`/supplier/projects/all`, {replace: true})
            }
        },
        500, [filters]
    );
    const {
        data: opportunities,
        isFetching: isOppsLoading,
        error,
        refetch
    } = useAPI(['fetchAllSupplierOpportunities', debouncedFilters],  {
        onSuccess: async (data) => {
            // console.log('onSuccess', data);
            let clientNames = TB.uniq(TB.flatMap(data, 'client.name'));
            console.log(clientNames)
        },
        enabled: !talentResourceFilters?.isLoading && !!status && auth?.canManageClientResourceTalent()
    })

    const isLoading = talentResourceFilters?.isLoading || isOppsLoading

    const resourceOptions = useMemo(() => {
        return TB.map(talentResourceFilters?.data || [], user => {

            // return (
            //     <Select.Option
            //         value={user.id}
            //         disabled={!user.talent_discipline_ids?.length}>
            //
            //     </Select.Option>
            // )
            return {

                label: user.fullName(),
                value: user.id,
                disabled: !user.talent_discipline_ids?.length
            }
        })
    }, [talentResourceFilters?.data, talentResourceFilters?.isLoading]);

    const tabs = useMemo(() => {
        let [availableOpps, onHoldOpps] = TB.partition(opportunities || [], ['project.is_on_hold', false]);
        let oppsByStatus = TB.groupBy(availableOpps, opp => opp.displayStatus?.());

        let options = TB.reduce(Opportunity.statusOptions(), (result, key) => {
            result[key] = (oppsByStatus[key] || []).length;
            return result
        }, {
            'All': opportunities?.length || 0,
        });
        options['On-Hold'] = onHoldOpps.length;

        return options;
    }, [status, opportunities, isLoading]);

    const statusFromQuery = useMemo(() => {
        if (!status) return;
        return TB.find(OppStatuses, oppStatus => TB.slugify(oppStatus) === status) || 'All'
    }, [status]);

    const groupedOppsByProject = useMemo(() => {
        if (isLoading || !opportunities) return [];
        let normalized = TB.map(opportunities, opportunity => {
            opportunity.display_status = opportunity.displayStatus();
            return opportunity;
        });

        normalized = TB.orderBy(normalized, opp => opp.project_position?.posted_at || opp.created_at, 'asc');
        let [availableOpps, onHoldOpps] = TB.partition(normalized, ['project.is_on_hold', false]);

        switch (statusFromQuery) {
            case 'All':
                break;
            case 'On-Hold':
                normalized = onHoldOpps;
                break;
            default:
                normalized = TB.filter(availableOpps, ['display_status', statusFromQuery]);
                break;
        }

        return TB.orderBy(
            TB.map(TB.groupBy(normalized, 'project.id'), (opps, projectId) => {
                return {
                    projectId: projectId,
                    opps: TB.orderBy(opps, opp => opp.project_position?.posted_at || opp.created_at, 'asc'),
                    posted_at: opps[0].project_position?.posted_at || opps[0].created_at
                }
            }), 'posted_at', 'desc'
        );

    }, [status, isLoading, opportunities]);

    const emptyMessage = useMemo(() => {
        switch (statusFromQuery) {
            case 'All':
                return "No Projects Found"
            case 'On-Hold':
                return "No Projects On-Hold Found"
            default:
                return `No ${statusFromQuery} Projects Found`;
        }
    }, [status, isLoading, opportunities]);

    const selectStatus = (selectedStatus) => {
        if (selectedStatus === status || !OppStatuses.includes(selectedStatus)) {
            return;
        }

        return navigate(`/supplier/projects/${TB.slugify(selectedStatus)}`, {replace: true})
    }

    if (!status) {
        return <Navigate to={generatePath('/supplier/projects/:status', {status: 'all'})}/>
    }

    return (
        <div className="flex flex-col items-stretch justify-start flex-grow">
            {tabs && (
                <div className="py-6 bg-gray project-status-tabs">
                    <SimpleStatusTabs
                        tabs={tabs}
                        isDisabled={isLoading}
                        buttonStyles={{width: 'unset'}}
                        activeTab={statusFromQuery}
                        onChange={selectStatus}/>
                </div>
            )}

            <div className="w-full flex-grow talent-portal-page-container flex-col items-stretch mx-auto">
                <div className="my-4 flex flex-row items-center gap-4">
                    {!talentResourceFilters.isLoading && resourceOptions?.length > 0 && (
                        <div className="flex flex-col gap-1 items-stretch basis-1/3">
                            <label>Resources</label>
                            <Select
                                filterOption={true}
                                mode="multiple"
                                value={filters.matching_talent_users}
                                onChange={(value) => {
                                    setFilters({search: '', matching_talent_users: value || []})
                                }}
                                notFoundContent="Filter Talent Resources"
                                placeholder="Select resources"
                                optionFilterProp="label"
                                // notFoundContent={fetching ? <Spin size="small" /> : null}
                                options={resourceOptions}
                            />
                        </div>
                    )}
                    {!talentResourceFilters.isLoading && resourceOptions?.length > 0 && (
                        <div className="flex flex-col gap-1 items-stretch basis-1/3">
                            <label>Search</label>
                            <Input
                                placeholder="Search by client, project, or position"
                                value={filters.search}
                                allowClear={true}
                                onChange={event => setFilters({...filters, search: event.currentTarget?.value})}
                                maxLength={50}
                            />

                        </div>
                    )}
                </div>
                {isLoading && (
                    <div className="flex-grow flex items-center justify-center">
                        <LoadingBanner message={(
                            debouncedFilters.matching_talent_users?.length || debouncedFilters.search.length
                        ) ? 'Searching' : 'Loading Project Opportunities'}/>
                    </div>
                )}

                {TB.isObject(error) && (
                    <div className="flex-grow flex items-center justify-center">
                        <ErrorBanner onRetry={refetch}/>
                    </div>
                )}
                {!isLoading && !TB.isObject(error) && (
                    <div className="flex-grow">
                        {groupedOppsByProject?.length > 0 && (
                            <p className="font-semibold my-4 whitespace-nowrap">
                                {TB.flatMap(groupedOppsByProject, 'opps').length} Results
                            </p>
                        )}
                        <div className="divide-wrippleLightGrayBg divide-y divide-solid">

                            {groupedOppsByProject?.length > 0 ? TB.map(groupedOppsByProject, (groupedProject) => {

                                const {opps, projectId, posted_at} = groupedProject;

                                const {client, project} = TB.first(opps);
                                return (
                                    <div key={projectId} className="my-6 py-6 px-3 bg-white">
                                        <div className="flex items-center justify-between ">
                                            <div className="flex items-center">

                                                <img src={client.logo_path}
                                                     className="h-16 w-16 object-cover border border-solid border-wripplePrimaryRuleBlue p-1"/>

                                                <div className="pl-3">
                                                    <p className="text-lg font-semibold">{project.name}</p>
                                                    <p>{client.name}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <p className="text-xs">
                                                    <span className="font-medium">Client Posted:</span>
                                                    &nbsp;
                                                    {TB.moment(posted_at).format('M/d/YY')}
                                                </p>
                                                <p className="text-xs">
                                                    <span className="font-medium">Last Client Active:</span>
                                                    &nbsp;
                                                    {TB.moment(project.latest_client_activity_at).format('M/d/YY')}
                                                </p>
                                            </div>

                                        </div>
                                        {opps.map(opp => <AgencyOppRow opportunity={opp} key={opp.id}/>)}
                                    </div>
                                )
                            }) : (
                                <EmptyBanner style={{paddingTop: '200px'}} message={emptyMessage}/>
                            )}
                        </div>
                    </div>
                )}
                <Footer/>
            </div>
        </div>
    )
}

const AgencyOppRow = ({opportunity}) => {

    const canManage = !opportunity.isOnHold() && opportunity.isAvailable();
    const project_position = opportunity.project_position;
    useMount(() => {
        if (opportunity.is_for_team) {

        }
    })

    const badgeInfo = useMemo(() => {
        let classNames = 'rounded-full py-1 px-3 ';
        let label;
        let timeSince;
        let style = {};
        if (opportunity.isOnHold()) {
            classNames += 'bg-[#ececec]'
            label = 'Project On Hold'
            timeSince = TB.moment(opportunity.created_at).fromNow()
        }  else if (!opportunity.isAvailable()) {
            classNames += 'bg-danger text-white'
            label = 'Lost/Declined'
            timeSince = TB.moment(opportunity.withdrew_at).fromNow()
        } else if (!opportunity.accepted_at) {
            classNames += 'bg-warning text-white'
            label = 'Brief Needs Review'
            timeSince = TB.moment(opportunity.created_at).fromNow()
        } else if (opportunity.is_for_team) {
            classNames += ` text-white`
            style.backgroundColor = opportunity.statusColor()
            label = opportunity.status;
            timeSince = TB.moment(opportunity.updated_at).fromNow()
        } else if (opportunity.has_unanswered_invite_user_opps) {
            classNames += 'bg-[#4B80E7] text-white';
            label = 'Invited/New';
            timeSince = TB.moment(opportunity.created_at).fromNow()
        } else if(opportunity.has_accepted_candidates) {
            if(opportunity.display_status === 'Contract') {
                classNames += 'bg-black text-white';
                label = 'Contract Ready to Sign!';
                timeSince = TB.moment(opportunity.created_at).fromNow()
            }else {
                classNames += 'bg-secondary text-white';
                label = 'Proposing';
                timeSince = TB.moment(opportunity.created_at).fromNow()
            }
        }else {
            classNames += 'bg-wrippleSuccessLight text-white';
            label = 'Brief Accepted';
            timeSince = TB.moment(opportunity.accepted_at).fromNow()
        }

        return {
            classNames,
            label,
            style,
            timeSince
        }
    }, [opportunity])

    // has_accepted_candidates
    // has_interested_uninvited_candidates
    // has_invited_candidates
    // has_unanswered_invite_user_opps
    // interested_candidates_count
    // invited_candidates_count
    // matching_talent_count
    // is_dead

    return (
        <div key={opportunity.id}
             className="bg-white border rounded-lg border-wripplePrimaryRuleBlue mt-4 overflow-hidden">

            <div className="flex justify-between">
                <div className="flex p-3 gap-8 items-center">
                    <Link to={opportunity.detailLink()}
                          className={TB.mapClass("flex flex-row items-center gap-1", {
                              'pointer-events-none': !canManage
                          })} onClick={e => !canManage && e.preventDefault()}>
                        <div className={TB.mapClass("overflow-hidden", {
                            "max-w-[450px]": project_position,
                            "max-w-full": opportunity.is_for_team
                        })}>
                            <h4 className="text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                                {project_position ? project_position.title : opportunity.project.service.name}
                            </h4>
                        </div>
                    </Link>

                    {project_position && (
                        <p className="font-light">{TB.formatCurrency(project_position.estimated_payout, false)} budget</p>
                    )}
                </div>

                <div className="flex p-3 gap-8 items-center text-xs">

                    <div className={badgeInfo.classNames} style={badgeInfo.style}>
                        {badgeInfo.label}
                    </div>
                    <p className="text-wripplePrimaryDarkNavy">
                        {badgeInfo.timeSince}
                    </p>

                    <React.Fragment>
                        <div className="flex w-[71px]">
                            <button disabled={canManage} type="button" className="p-3 relative">
                                {!!opportunity.unread_messages_count && (
                                    <Badge count={opportunity.unread_messages_count}
                                           size="small"
                                           className="absolute top-0 right-0.5"/>
                                )}
                                <i className="fas fa-comment-alt text-light-gray6"/>
                            </button>
                            <button disabled={canManage} type="button" className="p-3 relative">
                                {(!!opportunity.upcoming_meetings_count || !!opportunity.proposed_meetings_count) && (
                                    <Badge
                                        count={opportunity.upcoming_meetings_count + opportunity.proposed_meetings_count}
                                        size="small"
                                        className="absolute top-0 right-0.5"/>
                                )}
                                <i className="fas fa-calendar-alt text-light-gray6"/>
                            </button>
                        </div>
                    </React.Fragment>
                    <div className="w-[74px]">
                        {canManage ? (
                            <Link to={opportunity.detailLink()}
                                  className="btn btn-primary">
                                Manage
                            </Link>
                        ) : (
                            <button disabled className="btn btn-primary">
                                Manage
                            </button>
                        )}
                    </div>

                </div>
            </div>

            <div className="flex bg-wrippleOffWhite p-3 justify-between">

                    {opportunity.is_for_team ? (
                        <div className="flex gap-8">
                            <p className="text-secondary font-medium">Team Project</p>
                            <p>{opportunity.invited_candidates_count} Invited</p>
                            <p>{opportunity.interested_candidates_count} Proposing</p>
                            {opportunity.hasUnseenUpdate() && (
                                <p className="px-1.5 py-0.5 bg-danger flex items-center justify-center rounded-xl text-white text-[9px]">
                                    UPDATED!
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="flex gap-8">
                            <p className="text-secondary font-medium">Hire By Role</p>
                            <p>{TB.pluralize("Match", opportunity.matching_talent_count, true)}</p>
                            <p>{opportunity.interested_candidates_count} Interested</p>
                            <p>{opportunity.invited_candidates_count} Invited</p>
                            {opportunity.hasUnseenUpdate() && (
                                <p className="px-1.5 py-0.5 bg-danger flex items-center justify-center rounded-xl text-white text-[9px]">
                                    UPDATED!
                                </p>
                            )}
                        </div>
                    )}

            </div>
        </div>
    )
}

export {
    AgencyOpportunityList
}